import { Check, RotateCcw } from 'lucide-react';
import { useCheckedIngredients } from '@/lib/checked';
import { joinIngredient } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Ingredient } from '@shared/schemas';

interface Props {
  recipeId: number;
  ingredients: Ingredient[];
}

export default function IngredientList({ recipeId, ingredients }: Props) {
  const { checked, toggle, clear } = useCheckedIngredients(recipeId);

  if (ingredients.length === 0) {
    return <p className="text-sm text-text-muted">Zatím žádné suroviny.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Suroviny</h2>
        {checked.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-text-muted hover:bg-accent-soft hover:text-accent"
          >
            <RotateCcw size={12} />
            Vyčistit ({checked.size})
          </button>
        )}
      </div>
      <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface">
        {ingredients.map((ing, idx) => {
          const isChecked = checked.has(idx);
          const display = joinIngredient(ing);
          return (
            <li key={idx}>
              <button
                type="button"
                onClick={() => toggle(idx)}
                aria-pressed={isChecked}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                  isChecked ? 'bg-accent-soft/40' : 'hover:bg-accent-soft/30',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                    isChecked
                      ? 'border-accent bg-accent text-white'
                      : 'border-border bg-surface-elevated',
                  )}
                  aria-hidden
                >
                  {isChecked && <Check size={14} strokeWidth={3} />}
                </span>
                <span
                  className={cn(
                    'flex-1 text-[15px] leading-snug',
                    isChecked && 'text-text-muted line-through',
                  )}
                >
                  {display}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
